// Get the API URL
const apiUrl = document.querySelector("#api-url").value;

// === Utility Functions ===
function formatPrice(price) {
  return price.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
  });
}

// === Dialog Manager ===
class DialogManager {
  constructor() {
    this.dialog = document.getElementById("generic-dialog");
    this.titleElement = document.getElementById("dialog-title");
    this.bodyElement = document.getElementById("dialog-body");
    this.actionsContainer = document.getElementById("dialog-actions");
    this.closeButton = document.getElementById("close-generic-dialog");

    if (this.dialog && this.closeButton) {
      this.closeButton.onclick = () => this.dialog.close();
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) {
          this.dialog.close();
        }
      });
    }
  }

  show(title, message, actionsConfig = null) {
    if (!this.dialog || !this.titleElement || !this.bodyElement || !this.actionsContainer) {
      alert(message);
      return;
    }

    this.titleElement.textContent = title;
    this.bodyElement.innerHTML = message;

    this.actionsContainer.innerHTML = "";

    if (actionsConfig && Array.isArray(actionsConfig)) {
      actionsConfig.forEach((action) => {
        const button = document.createElement("button");
        button.className = action.className || "uppercase font-bold bg-primary text-secondary px-4 py-2 rounded hover:bg-primary/90 transition-colors cursor-pointer";
        button.textContent = action.text;

        if (action.onclick) {
          button.onclick = action.onclick;
        } else if (action.href) {
          button.onclick = () => (window.location.href = action.href);
        } else {
          button.onclick = () => this.dialog.close();
        }

        this.actionsContainer.appendChild(button);
      });
    } else {
      const okButton = document.createElement("button");
      okButton.className = "uppercase font-bold bg-primary text-secondary px-4 py-2 rounded hover:bg-primary/90 transition-colors cursor-pointer";
      okButton.textContent = "OK";
      okButton.onclick = () => this.dialog.close();
      this.actionsContainer.appendChild(okButton);
    }

    this.dialog.showModal();
  }

  showError(message) {
    this.show("Error", message);
  }

  showSuccess(title = "Success", message, actions = null) {
    this.show(title, message, actions);
  }
}

// === Product Variant Manager ===
class ProductVariantManager {
  constructor(dialogManager) {
    this.dialogManager = dialogManager;
    this.selectedOptions = {};
    this.variantSelect = document.querySelector("select#variant");
    this.priceBottom = document.querySelector("#price-bottom");
    this.quantityBottomElements = document.querySelectorAll(".friendly-bottom-quantity");
    this.sizeBottomElements = document.querySelectorAll(".friendly-bottom-size");
    // Inline elements
    this.inlineContainer = document.querySelector("#inline-selected-options");
    this.inlinePrice = document.querySelector("#inline-price");
    this.inlineInfoElements = {};
    this.matchedVariant = {};
  }

  // Initialize for standard product pages (with swatches)
  initStandardProduct() {
    if (typeof product === "undefined") return;

    this.selectedOptions = Array(product.options.length).fill("");
    this.inlineSize = document.querySelector("#inline-size");
    this.inlineQuantity = document.querySelector("#inline-quantity");

    // Handle URL parameters
    const urlVariant = this.getVariantFromUrl();
    if (urlVariant) {
      this.preselectSwatchesFromUrl(urlVariant);
      this.variantSelect.value = urlVariant.id;
      this.updatePriceDisplay(this.variantSelect);
    } else {
      this.initializeFirstSwatches();
    }

    // Handle swatch clicks
    document.querySelectorAll(".variant-swatch").forEach((swatch) => {
      if (swatch.dataset.optionName === "Quantity") {
        swatch.textContent = parseInt(swatch.dataset.value).toLocaleString();
      }
      swatch.addEventListener("click", () => this.handleSwatchClick(swatch));
    });

    // Handle variant select changes
    if (this.variantSelect) {
      this.variantSelect.addEventListener("input", (event) => {
        this.updatePriceDisplay(event.target);
        const selectedOption = event.target.options[event.target.selectedIndex];
        const size = selectedOption.dataset.option1;
        const qty = selectedOption.dataset.option2;
        this.updateUrlWithVariant(size, qty);
      });
    }
  }

  // Initialize for collection pages (flyers with dynamic buttons)
  initCollectionProduct(products) {
    if (!products || products.length === 0) return;

    const productForm = document.querySelector("form#add-product");
    const variantContainerElement = document.querySelector("#variant-container");

    // Store products for later use in validation
    this.products = products;

    products.forEach((product) => {
      optionsToDisplay.forEach((option) => {
        let optionContainer = variantContainerElement.querySelector(`[data-option="${option}"]`);
        if (!optionContainer) {
          optionContainer = this.createOptionContainer(option, variantContainerElement);
          let inlineElement = document.createElement("span");
          inlineElement.id = "inline-" + option;
          this.inlineContainer.appendChild(inlineElement);
          this.inlineInfoElements[option] = inlineElement;
        }

        let optionButton = optionContainer.querySelector(`[data-value="${product[option]}"]`);
        if (!optionButton) {
          optionButton = this.createOptionButton(product[option], optionContainer);
          optionButton.addEventListener("click", (event) => {
            this.handleCollectionButtonClick(event, option, product[option], products, productForm, optionButton);
          });

          // Check URL for pre-selection
          const url = new URL(window.location);
          if (url.searchParams.has(option) && decodeURIComponent(url.searchParams.get(option)).replace("_", " ") === product[option]) {
            optionButton.click();
          }
        }
      });
    });

    // Auto-select first option for each if not already selected
    optionsToDisplay.forEach((option) => {
      if (!this.selectedOptions[option]) {
        const firstButton = variantContainerElement.querySelector(`[data-option="${option}"] button:first-child`);
        if (firstButton) firstButton.click();
      }
    });

    // Update button states based on current selections
    this.updateOptionButtonStates();
  }

  createOptionContainer(option, container) {
    const template = document.querySelector("[data-tpl=option]");
    const optionContainer = template.cloneNode(true);
    optionContainer.removeAttribute("data-tpl");
    optionContainer.setAttribute("data-option", option);
    optionContainer.querySelector("label").textContent = option;
    container.appendChild(optionContainer);
    return optionContainer;
  }

  createOptionButton(value, container) {
    const template = document.querySelector('[data-tpl="option-value"]');
    const button = template.cloneNode(true);
    button.removeAttribute("data-tpl");
    button.setAttribute("data-value", value);
    button.textContent = container.dataset.option == "Quantity" ? parseInt(value).toLocaleString("en-GB") : value;
    container.querySelector("[data-prop=option]").appendChild(button);
    return button;
  }

  // Update button states based on valid product combinations
  updateOptionButtonStates() {
    if (!this.products) return;

    // For each option type, check which values are valid given current selections
    optionsToDisplay.forEach((option) => {
      const optionContainer = document.querySelector(`[data-option="${option}"]`);
      if (!optionContainer) return;

      const optionButtons = optionContainer.querySelectorAll("button");
      optionButtons.forEach((button) => {
        const value = button.getAttribute("data-value");

        // Temporarily update selected options to test this combination
        const originalValue = this.selectedOptions[option];
        this.selectedOptions[option] = value;

        // Check if any product matches the current selections
        const hasMatchingProduct = this.products.some((product) =>
          optionsToDisplay.every((param) => {
            if (this.selectedOptions[param] === undefined) return true;
            return product[param] === this.selectedOptions[param];
          }),
        );

        // Restore original value
        this.selectedOptions[option] = originalValue;

        // Enable/disable the button based on whether there's a matching product
        if (hasMatchingProduct) {
          button.disabled = false;
          button.classList.remove("opacity-50", "cursor-not-allowed");
          button.classList.add("hover:bg-primary", "cursor-pointer");
        } else {
          button.disabled = true;
          button.classList.add("opacity-50", "cursor-not-allowed");
          button.classList.remove("hover:bg-primary", "cursor-pointer");
        }
      });
    });
  }

  handleSwatchClick(swatch) {
    if (swatch.disabled) return;

    const optionIndex = parseInt(swatch.dataset.option);
    const value = swatch.dataset.value;

    // Update UI
    document.querySelectorAll(`.variant-swatch[data-option="${optionIndex}"]`).forEach((s) => {
      s.classList.remove("bg-primary", "text-secondary", "border-primary");
      s.classList.add("hover:bg-primary/10");
    });

    swatch.classList.remove("hover:bg-primary/10");
    swatch.classList.add("bg-primary", "text-secondary", "border-primary");

    this.selectedOptions[optionIndex] = value;

    // Find matching variant
    const matchedVariant = this.findMatchingVariant();
    if (matchedVariant) {
      this.matchedVariant = matchedVariant;
      this.variantSelect.value = matchedVariant.id;
      this.updatePriceDisplay(this.variantSelect);

      // Update table cell selection
      document.querySelectorAll(".variant-cell").forEach((cell) => {
        cell.classList.remove("bg-primary/30");
        if (cell.dataset.variantId == matchedVariant.id) {
          cell.classList.add("bg-primary/30");
        }
      });

      // Update URL
      const size = matchedVariant.options[0];
      const qty = matchedVariant.options[1];
      this.updateUrlWithVariant(size, qty);

      // Trigger image upload refresh for variant change
      this.refreshImageUploads();
    }
  }

  handleCollectionButtonClick(event, option, value, products, productForm, optionButton) {
    event.preventDefault();

    // Don't process if button is disabled
    if (optionButton.disabled) return;

    this.selectedOptions[option] = value;

    // Update inline selected options
    if (this.inlineInfoElements[option]) this.inlineInfoElements[option].textContent = `${option}: ${optionButton.textContent}`;

    const container = event.target.closest("[data-option]");
    container.querySelectorAll("button").forEach((btn) => {
      btn.classList.remove("bg-primary", "selected");
    });
    event.target.classList.add("bg-primary", "selected");

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set(option, encodeURIComponent(value.replace(" ", "_")));
    window.history.replaceState({}, "", url);

    // Find matching variant
    const matched = products.filter((item) =>
      Object.keys(this.selectedOptions).every((param) => {
        if (this.selectedOptions[param] === undefined) return true;
        return item[param] === this.selectedOptions[param];
      }),
    );

    if (matched.length > 0) {
      const variant = matched[0];
      const formattedPrice = "£" + (variant.Price / 100).toLocaleString();
      productForm.querySelector("[name=id]").value = variant.id;
      if (this.priceBottom) this.priceBottom.textContent = formattedPrice;
      if (this.inlinePrice) this.inlinePrice.textContent = formattedPrice;

      this.matchedVariant = variant;
    }

    // Update button states for other options based on current selection
    this.updateOptionButtonStates();

    // Trigger image upload refresh for variant change
    this.refreshImageUploads();
  }

  findMatchingVariant() {
    for (const variant of product.variants) {
      let match = true;
      for (let i = 0; i < this.selectedOptions.length; i++) {
        if (this.selectedOptions[i] && variant.options[i] !== this.selectedOptions[i]) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }
    return null;
  }

  updatePriceDisplay(element) {
    if (!element) return;

    const selectedOption = element.options[element.selectedIndex];
    const newPrice = selectedOption.getAttribute("data-price");
    const friendlyPrice = formatPrice(newPrice / 100);
    const size = selectedOption.dataset.option1;
    const qty = selectedOption.dataset.option2;

    if (this.priceBottom) this.priceBottom.textContent = friendlyPrice;

    if (this.quantityBottomElements) {
      this.quantityBottomElements.forEach((e) => (e.textContent = "x" + qty));
    }

    if (this.sizeBottomElements) {
      this.sizeBottomElements.forEach((e) => (e.textContent = size));
    }

    // Update inline elements
    if (this.inlinePrice) this.inlinePrice.textContent = friendlyPrice;
    if (this.inlineSize) this.inlineSize.textContent = "Size: " + size;
    if (this.inlineQuantity) this.inlineQuantity.textContent = "Quantity: x" + qty;

    if (size && qty) {
      this.updateUrlWithVariant(size, qty);
    }
  }

  updateUrlWithVariant(size, quantity) {
    if (!size || !quantity) return;

    const cleanSize = encodeURIComponent(size.replace(/\s+/g, "-").toLowerCase());
    const cleanQuantity = encodeURIComponent(quantity.replace(/\s+/g, "-").toLowerCase());

    const url = new URL(window.location);
    url.searchParams.set("size", cleanSize);
    url.searchParams.set("qty", cleanQuantity);
    window.history.replaceState({}, "", url);
  }

  getVariantFromUrl() {
    const url = new URL(window.location);
    const sizeParam = url.searchParams.get("size");
    const qtyParam = url.searchParams.get("qty");

    if (!sizeParam || !qtyParam) return null;

    const cleanSize = decodeURIComponent(sizeParam).replace(/-/g, " ");
    const cleanQty = decodeURIComponent(qtyParam).replace(/-/g, " ");

    for (const variant of product.variants) {
      if (variant.options[0].toLowerCase() === cleanSize.toLowerCase() && variant.options[1].toLowerCase() === cleanQty.toLowerCase()) {
        return variant;
      }
    }
    return null;
  }

  preselectSwatchesFromUrl(urlVariant) {
    urlVariant.options.forEach((optionValue, index) => {
      if (optionValue) {
        const swatch = document.querySelector(`.variant-swatch[data-option="${index}"][data-value="${optionValue}"]`);
        if (swatch) {
          document.querySelectorAll(`.variant-swatch[data-option="${index}"]`).forEach((s) => {
            s.classList.remove("bg-primary", "text-secondary", "border-primary");
          });
          swatch.classList.add("bg-primary", "text-secondary", "border-primary");
          this.selectedOptions[index] = optionValue;
        }
      }
    });
  }

  initializeFirstSwatches() {
    product.options.forEach((option, index) => {
      const firstSwatch = document.querySelector(`.variant-swatch[data-option="${index}"]:not([disabled])`);
      if (firstSwatch) {
        firstSwatch.classList.add("bg-primary", "text-secondary", "border-primary");
        this.selectedOptions[index] = firstSwatch.dataset.value;
      }
    });
  }

  refreshImageUploads() {
    // Clear existing image uploads when variant changes
    const imageUploadContainers = document.querySelectorAll(".image-upload");

    if (window.imageUploadManager) {
      window.imageUploadManager.clearProductImageData();
    }

    imageUploadContainers.forEach((container) => {
      if (!container.closest(".cart-row")) {
        // Only clear non-cart containers
        const pageCount = parseInt(this.matchedVariant.Page_Count);
        container.dataset.pageCount = pageCount;
        container.setAttribute("data-page-count", pageCount);

        // Update the SKU
        container.dataset.sku = this.matchedVariant.SKU;
        container.setAttribute("data-sku", this.matchedVariant.SKU);

        const imageUploadBlocks = container.querySelectorAll(".image-upload-block");

        imageUploadBlocks.forEach((block, blockIndex) => {
          const imageReturn = block.querySelector(".image-upload-return");
          /*
          const imagePreview = block.querySelector(".image-upload-preview");
          const imagePreviewContainer = block.querySelector(".image-upload-preview-container");
          const imageUploadAction = block.querySelector(".image-upload-action");
          const imageRemove = block.querySelector(".image-upload-remove");

          // Reset UI
          if (imagePreview) imagePreview.innerHTML = "";
          if (imagePreviewContainer) imagePreviewContainer.classList.add("hidden");
          if (imageUploadAction) imageUploadAction.classList.remove("hidden");
          if (imageRemove) imageRemove.classList.add("hidden");
          if (imageReturn) imageReturn.value = "";
          if (block.querySelector('input[type="file"]')) {
            block.querySelector('input[type="file"]').value = "";
          }
          block.classList.remove("loaded");

          // Clear URL parameter for this image
          if (window.imageUploadManager) {
            window.imageUploadManager.updateUrlWithImage(block.dataset.index, "");
          }
          */

          // Hide this block if not needed for this variant
          const uniqueIdKey = "__uniqueID_" + blockIndex;
          if (blockIndex + 1 > pageCount) {
            block.classList.add("hidden");
          } else {
            block.classList.remove("hidden");
            if (window.imageUploadManager && imageReturn.value != "") {
              window.imageUploadManager.productImageData[uniqueIdKey] = imageReturn.value;
            }
          }
        });
      }
    });

    // Clear product image data
    /*
      if (window.imageUploadManager) {
      window.imageUploadManager.clearProductImageData();
    }
    */
  }

  // Handle variant cell clicks (for tables)
  initVariantCells() {
    document.querySelectorAll(".variant-cell").forEach((cell) => {
      cell.addEventListener("click", () => {
        this.variantSelect.value = cell.dataset.variantId;
        this.updatePriceDisplay(this.variantSelect);

        document.querySelectorAll(".variant-cell").forEach((c) => c.classList.remove("bg-primary/30"));
        cell.classList.add("bg-primary/30");
      });
    });
  }
}

// === Image Upload Manager ===
class ImageUploadManager {
  constructor(dialogManager) {
    this.dialogManager = dialogManager;
    this.cartItemProperties = {};
    this.requiredImageCount = 0;
    this.productImageData = {}; // Store image data for product pages
  }

  init() {
    const imageUploadContainers = document.querySelectorAll(".image-upload");

    imageUploadContainers.forEach((container) => {
      const imageUploadBlocks = container.querySelectorAll(".image-upload-block");
      this.requiredImageCount += parseInt(container.dataset.pageCount);

      imageUploadBlocks.forEach((block) => {
        const imageInputElement = block.querySelector(".image-upload-file");
        const imagePreviewContainer = block.querySelector(".image-upload-preview-container");
        const imagePreview = block.querySelector(".image-upload-preview");
        const imageRemove = block.querySelector(".image-upload-remove");
        const imageUploadAction = block.querySelector(".image-upload-action");
        const imageReturn = block.querySelector(".image-upload-return");

        // Handle cart page items (with line data)
        if (block.dataset.line) {
          const propertyKey = `__uniqueID_${imageReturn.dataset.index}`;
          if (!this.cartItemProperties[block.dataset.line]) {
            this.cartItemProperties[block.dataset.line] = {};
          }
          if (block.dataset.uniqueId !== "") {
            this.cartItemProperties[block.dataset.line][propertyKey] = block.dataset.uniqueId;
          }
        }

        block.dataset.sku = container.dataset.sku;

        if (imageUploadAction) {
          imageUploadAction.addEventListener("click", () => imageInputElement.click());
        }

        if (imageInputElement) {
          imageInputElement.addEventListener("input", (event) => {
            this.handleImageUpload(event, block, imagePreview, imageUploadAction, imageRemove, imagePreviewContainer, imageReturn);
          });
        }

        if (imageRemove) {
          imageRemove.addEventListener("click", (event) => {
            this.handleImageRemove(event, block, imagePreview, imageUploadAction, imageRemove, imagePreviewContainer, imageReturn);
          });
        }
      });
    });

    // Check URL parameters for pre-existing image data on product pages
    this.loadImagesFromUrl();
  }

  async handleImageUpload(event, block, imagePreview, imageUploadAction, imageRemove, imagePreviewContainer, imageReturn) {
    event.preventDefault();

    const formData = new FormData();
    const imageFile = block.querySelector('input[type="file"]').files[0];
    formData.append("sku", block.dataset.sku);
    formData.append("image", imageFile);
    formData.append("slot", parseInt(block.dataset.index));

    try {
      const response = await fetch(apiUrl + "api/upload", {
        method: "POST",
        body: formData,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      const data = await response.json();

      if (data.success) {
        console.log("Upload successful");

        const uploadedImage = document.createElement("img");
        uploadedImage.src = apiUrl + data.preview;
        uploadedImage.width = 150;
        uploadedImage.dataset.uniqueId = data.uniqueID;

        imagePreview.appendChild(uploadedImage);
        imagePreview.classList.remove("hidden");
        imageRemove.classList.remove("hidden");
        imagePreviewContainer.classList.remove("hidden");
        imageUploadAction.classList.add("hidden");
        imageReturn.value = data.uniqueID;
        block.classList.add("loaded");

        // Handle cart page items
        if (block.dataset.line) {
          if (!this.cartItemProperties[imageReturn.dataset.line]) {
            this.cartItemProperties[imageReturn.dataset.line] = {};
          }

          const propertyKey = `__uniqueID_${imageReturn.dataset.index}`;
          this.cartItemProperties[block.dataset.line][propertyKey] = data.uniqueID;

          await this.updateCartProperties(block.dataset.line);
        } else {
          // Handle product page items
          const propertyKey = `__uniqueID_${imageReturn.dataset.index}`;
          this.productImageData[propertyKey] = data.uniqueID;

          // Update URL to include image data
          this.updateUrlWithImage(imageReturn.dataset.index, data.uniqueID);
        }
      } else {
        this.dialogManager.showError("Upload failed: " + data.message);
      }
    } catch (error) {
      console.error("AJAX error:", error);
      this.dialogManager.showError("Upload failed.<br>Please try again.");
    }
  }

  async handleImageRemove(event, block, imagePreview, imageUploadAction, imageRemove, imagePreviewContainer, imageReturn) {
    event.preventDefault();
    const imageToRemove = imagePreview.querySelector("img");
    const propertyKey = `__uniqueID_${imageReturn.dataset.index}`;
    const uniqueID = imageToRemove.dataset.uniqueId;

    delete this.productImageData[propertyKey];

    imagePreview.innerHTML = "";
    imagePreview.classList.add("hidden");
    imageUploadAction.classList.remove("hidden");
    imageRemove.classList.add("hidden");
    block.querySelector('input[type="file"]').value = "";
    imageReturn.value = "";
    block.classList.remove("loaded");

    // Remove image data from URL
    this.updateUrlWithImage(imageReturn.dataset.index, "");
    /*
    try {
      const response = await fetch(apiUrl + "api/delete", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ uniqueID: uniqueID, token: "not-a-real-token" }),
      });

      const data = await response.json();

      if (data.success) {
        imagePreview.innerHTML = "";
        imagePreview.classList.add("hidden");
        imageUploadAction.classList.remove("hidden");
        imageRemove.classList.add("hidden");
        block.querySelector('input[type="file"]').value = "";
        imageReturn.value = "";
        block.classList.remove("loaded");

        const propertyKey = `__uniqueID_${imageReturn.dataset.index}`;

        // Handle cart page items
        if (block.dataset.line) {
          this.cartItemProperties[block.dataset.line][propertyKey] = "";
          await this.updateCartProperties(block.dataset.line);
        } else {
          // Handle product page items
          delete this.productImageData[propertyKey];

          // Remove image data from URL
          this.updateUrlWithImage(imageReturn.dataset.index, "");
        }
      } else {
        this.dialogManager.showError("Delete failed: " + data.message);
      }
    } catch (error) {
      console.error("AJAX error:", error);
      this.dialogManager.showError("Delete failed. Please try again.");
    }
   */
  }

  async updateCartProperties(line) {
    const updateData = {
      line: line,
      properties: this.cartItemProperties[line],
    };

    updateData.properties["_ts"] = Date.now();

    try {
      await fetch(window.Shopify.routes.root + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });
    } catch (error) {
      console.error("Cart update error:", error);
    }
  }

  getRequiredImageCount() {
    return this.requiredImageCount;
  }

  getUploadedImageCount() {
    return document.querySelectorAll(".image-upload-preview img").length;
  }

  getProductImageData() {
    return this.productImageData;
  }

  clearProductImageData() {
    this.productImageData = {};
  }

  // Check if all required images are uploaded for product pages
  areAllProductImagesUploaded() {
    const imageUploadContainers = document.querySelectorAll(".image-upload");
    let totalRequired = 0;
    let totalUploaded = Object.keys(window.imageUploadManager.productImageData).length;

    imageUploadContainers.forEach((container) => {
      if (!container.closest(".cart-row")) {
        // Only check non-cart containers
        totalRequired += parseInt(container.dataset.pageCount);
      }
    });

    return totalUploaded >= totalRequired;
  }

  loadImagesFromUrl() {
    const url = new URL(window.location);
    const imageUploadContainers = document.querySelectorAll(".image-upload");

    imageUploadContainers.forEach((container) => {
      if (container.closest(".cart-row")) return; // Skip cart containers

      const imageUploadBlocks = container.querySelectorAll(".image-upload-block");
      imageUploadBlocks.forEach((block) => {
        const index = block.dataset.index;
        const urlParam = `image_${index}`;
        const uniqueId = url.searchParams.get(urlParam);

        if (uniqueId) {
          // Display the pre-existing image
          const imagePreview = block.querySelector(".image-upload-preview");
          const imagePreviewContainer = block.querySelector(".image-upload-preview-container");
          const imageUploadAction = block.querySelector(".image-upload-action");
          const imageRemove = block.querySelector(".image-upload-remove");
          const imageReturn = block.querySelector(".image-upload-return");

          const uploadedImage = document.createElement("img");
          uploadedImage.src = apiUrl + "uploads/" + uniqueId + "/preview.jpg";
          uploadedImage.width = 150;
          uploadedImage.dataset.uniqueId = uniqueId;

          if (imagePreview) {
            imagePreview.appendChild(uploadedImage);
            imagePreview.classList.remove("hidden");
          }
          if (imageRemove) imageRemove.classList.remove("hidden");
          if (imagePreviewContainer) imagePreviewContainer.classList.remove("hidden");
          if (imageUploadAction) imageUploadAction.classList.add("hidden");
          if (imageReturn) imageReturn.value = uniqueId;
          block.classList.add("loaded");

          // Store in product image data
          const propertyKey = `__uniqueID_${index}`;
          this.productImageData[propertyKey] = uniqueId;
        }
      });
    });
  }

  updateUrlWithImage(index, uniqueId) {
    const url = new URL(window.location);
    const urlParam = `image_${index}`;

    if (uniqueId) {
      url.searchParams.set(urlParam, uniqueId);
    } else {
      url.searchParams.delete(urlParam);
    }

    window.history.replaceState({}, "", url);
  }
}

// === Cart Manager ===
class CartManager {
  constructor(dialogManager, imageUploadManager) {
    this.dialogManager = dialogManager;
    this.imageUploadManager = imageUploadManager;
  }

  init() {
    // Handle cart item removal
    document.querySelectorAll(".cart-remove-item-button").forEach((button) => {
      button.addEventListener("click", (event) => this.handleCartItemRemove(event, button));
    });

    // Handle cart form submission (checkout)
    const cartForm = document.querySelector("form#cart-form");
    if (cartForm) {
      cartForm.addEventListener("submit", (event) => this.handleCheckout(event));
    }

    // Handle empty cart click
    const emptyCartLink = document.querySelector("#empty-cart-link");
    if (emptyCartLink) {
      emptyCartLink.addEventListener("click", (event) => this.handleEmptyCartClick(event));
    }

    // Handle add to cart forms
    this.initAddToCartForms();

    this.updateHeaderCartAjax();
  }

  async handleEmptyCartClick(event) {
    event.preventDefault();

    this.dialogManager.show("Are you sure?", "This will remove all items from your cart and delete any uploaded images", [
      {
        text: "Cancel",
        className: "cursor-pointer uppercase font-bold bg-primary text-secondary px-4 py-2 rounded hover:bg-primary/90 transition-colors mr-4",
        onclick: () => this.dialogManager.dialog.close(),
      },
      {
        text: "Empty Cart",
        className: "cursor-pointer uppercase font-bold bg-secondary text-primary border border-primary px-4 py-2 rounded hover:bg-secondary/80 transition-colors",
        onclick: async () => {
          const response = await fetch("/cart/clear.js", {
            method: "POST",
          });

          if (response.ok) {
            window.location.href = "/cart";
          }
        },
      },
    ]);
  }

  async handleCartItemRemove(event, button) {
    event.preventDefault();

    const row = button.closest(".cart-row");
    const line = parseInt(row.dataset.line);
    const imagesToDelete = Object.values(this.imageUploadManager.cartItemProperties[line]).filter((data) => data !== "");

    try {
      // Delete associated images first
      // if (imagesToDelete.length > 0) {
      //   await fetch(apiUrl + "api/delete", {
      //     method: "DELETE",
      //     body: JSON.stringify({ uniqueID: imagesToDelete, token: "not-a-real-token" }),
      //     headers: { "X-Requested-With": "XMLHttpRequest" },
      //   });
      // }

      // Remove item from cart using change.js endpoint
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          line: line,
          quantity: 0,
        }),
      });

      if (response.ok) {
        // Remove the row from DOM
        row.remove();

        // Update cart totals and header
        await this.updateCartPage();
        this.updateHeaderCartAjax();

        const allCartRows = document.querySelectorAll(".cart-row");

        // Redirect if cart is empty
        if (allCartRows.length === 0) {
          window.location.href = "/cart";
        } else {
          allCartRows.forEach((row, rowIndex) => {
            row.dataset.line = rowIndex + 1;
            row.setAttribute("data-line", rowIndex + 1);
          });
        }
      } else {
        throw new Error("Failed to remove item from cart");
      }
    } catch (error) {
      console.error("Delete error:", error);
      this.dialogManager.showError("Failed to remove item. Please try again.");
    }
  }

  handleCheckout(event) {
    event.preventDefault();

    const attachedImageCount = this.imageUploadManager.getUploadedImageCount();
    const requiredImageCount = this.imageUploadManager.getRequiredImageCount();

    if (attachedImageCount < requiredImageCount) {
      this.dialogManager.showError("Some items still require artwork to be attached.");
      return;
    }

    window.location.href = "/checkout";
  }

  initAddToCartForms() {
    // Handle collection add to cart form (flyers)
    const addProductForm = document.querySelector("form#add-product");
    if (addProductForm) {
      addProductForm.addEventListener("submit", (event) => {
        event.preventDefault();
        this.addToCartAjax(new FormData(addProductForm));
      });
    }

    // Handle PDP add to cart form
    const pdpAddForm = document.querySelector("#pdp-add-form");
    if (pdpAddForm) {
      pdpAddForm.addEventListener("submit", (event) => {
        event.preventDefault();
        const shopifyForm = document.querySelector(".shopify-product-form");
        if (shopifyForm) {
          this.addToCartAjax(new FormData(shopifyForm));
        } else {
          this.dialogManager.showError("Unable to add item to cart. Form not found.");
        }
      });
    }
  }

  async addToCartAjax(formData) {
    // Validate image uploads for product pages
    if (!this.imageUploadManager.areAllProductImagesUploaded()) {
      this.dialogManager.showError("Please upload all required artwork images before adding to cart.");
      return;
    }

    try {
      const productImageData = this.imageUploadManager.getProductImageData();
      const properties = {
        __ts: Date.now(),
        ...productImageData,
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: formData.get("id"),
              quantity: formData.get("quantity") || 1,
              properties: properties,
            },
          ],
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.showCartSuccessDialog();
        this.updateHeaderCartAjax();
      } else {
        throw new Error("Failed to add item to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.dialogManager.showError("Failed to add item to cart. Please try again.");
    }
  }

  showCartSuccessDialog() {
    let successMessageHtml = "";

    // Show product info inline
    const inlineItemInfo = document.querySelectorAll("#inline-selected-options span");
    if (inlineItemInfo.length > 0) {
      if (collection.title != null) {
        successMessageHtml += `<strong class="font-bold">${collection.title}</strong><br>`;
      }
      inlineItemInfo.forEach((element) => {
        successMessageHtml += element.innerHTML + "<br>";
      });

      const inlinePriceHtml = document.querySelector("#inline-price").textContent;
      successMessageHtml += `Price: <strong class="font-bold">${inlinePriceHtml}</strong>`;
    }

    this.dialogManager.showSuccess("Added to Cart", successMessageHtml, [
      {
        text: "Continue Shopping",
        className: "cursor-pointer uppercase font-bold bg-secondary text-primary border border-primary px-4 py-2 rounded hover:bg-secondary/80 transition-colors mr-2",
        onclick: () => this.dialogManager.dialog.close(),
      },
      {
        text: "Go to Cart",
        className: "cursor-pointer uppercase font-bold bg-primary text-secondary px-4 py-2 rounded hover:bg-primary/90 transition-colors",
        href: "/cart",
      },
    ]);
  }

  async updateCartPage() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();

        // Update cart total on page
        const totalElement = document.querySelector(".text-xl.font-bold.text-secondary");
        if (totalElement) {
          totalElement.textContent = "Total: " + formatPrice(parseInt(data.total_price) / 100);
        }

        // Show empty cart message if no items
        if (data.items.length === 0) {
          const cartContainer = document.querySelector(".bg-white.text-secondary.rounded-lg");
          if (cartContainer) {
            cartContainer.innerHTML = '<div class="p-4"><h1 class="text-4xl font-bold uppercase text-secondary font-altivo">Cart</h1><p>There\'s nothing here!</p></div>';
          }
        }
      }
    } catch (error) {
      console.error("Could not update cart page:", error);
    }
  }

  async updateHeaderCartAjax() {
    const cartInfo = document.querySelector(".cart-info");
    const cartPrice = document.querySelector(".cart-total-price");
    const cartItemCount = document.querySelector(".cart-item-count");

    try {
      const response = await fetch("/cart.js", {
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        const item_count = data.item_count;

        if (item_count > 0) {
          const total_price = formatPrice(parseInt(data.items_subtotal_price) / 100);

          cartInfo.classList.remove("hidden");
          cartPrice.textContent = total_price;
          cartItemCount.textContent = item_count;
        } else {
          cartInfo.classList.add("hidden");
        }
      }
    } catch (error) {
      console.error("Could not update cart info");
    }
  }
}

// === Main Application ===
document.addEventListener("DOMContentLoaded", function () {
  // Initialize managers
  const dialogManager = new DialogManager();
  const productVariantManager = new ProductVariantManager(dialogManager);
  const imageUploadManager = new ImageUploadManager(dialogManager);
  const cartManager = new CartManager(dialogManager, imageUploadManager);

  window.productVariantManager = productVariantManager;

  // Initialize mobile menu
  const mobileMenuToggle = document.getElementById("mobile-menu-toggle");
  const mobileMenu = document.getElementById("mobile-menu");

  if (mobileMenuToggle && mobileMenu) {
    mobileMenuToggle.addEventListener("click", function () {
      mobileMenu.classList.toggle("hidden");
    });

    // Close mobile menu when clicking outside
    document.addEventListener("click", function (event) {
      if (!mobileMenu.classList.contains("hidden") && !mobileMenu.contains(event.target) && !mobileMenuToggle.contains(event.target)) {
        mobileMenu.classList.add("hidden");
      }
    });
  }

  // Initialize based on page type
  if (typeof products !== "undefined" && products.length > 0) {
    // Collection page (flyers)
    productVariantManager.initCollectionProduct(products);
  } else if (typeof product !== "undefined") {
    // Product page
    productVariantManager.initStandardProduct();
    productVariantManager.initVariantCells();

    // Update price display on load
    productVariantManager.updatePriceDisplay(productVariantManager.variantSelect);

    // Handle URL parameters
    const initialUrlVariant = productVariantManager.getVariantFromUrl();
    if (initialUrlVariant) {
      productVariantManager.updateUrlWithVariant(initialUrlVariant.options[0], initialUrlVariant.options[1]);
    }
  }

  // Initialize image uploads and cart functionality
  imageUploadManager.init();
  cartManager.init();

  // Make imageUploadManager globally accessible for variant changes
  window.imageUploadManager = imageUploadManager;
});
